// ─── Auth Provider ───
// React context providing authentication state and methods.

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { AuthenticatedUser } from '@a8-planner/shared';
import { authApi } from '../../lib/api/auth';
import { applyUserLanguage } from '../../lib/i18n';
import { useUIStore } from '../../stores/ui.store';

export interface AuthContextValue {
  user: AuthenticatedUser | null;
  /**
   * True when the caller is a designated vacation reviewer (ADR 0017 D6) for at
   * least one workspace, or a superadmin (issue #90). Populated by GET
   * /auth/me AND (freshness fix) atomically by POST /auth/callback and POST
   * /auth/dev-login via `setUser`'s second argument — every sign-in path
   * hydrates this without a separate round-trip. Gates the vacation-approver
   * inbox in RequestsHub.
   *
   * Optional (rather than required) so the dozens of pre-existing `useAuth()`
   * test mocks across the codebase that predate issue #90 don't all need an
   * unrelated update just to keep satisfying this interface — every REAL
   * `AuthProvider` instance always supplies a concrete boolean (default `false`).
   */
  isVacationReviewer?: boolean;
  /**
   * GHE #883 follow-up — whether there is ANY list where this person could create a task.
   * Gates the global "+" and the ⌘K create action, which have no list context.
   *
   * ⚠️ Not a substitute for the per-list `canCreateTask`: a list-scoped control must use that,
   * because this one is deliberately broader (a manager of one area can create SOMEWHERE, not
   * everywhere).
   */
  canCreateTaskAnywhere?: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * Kicks off the Entra ID redirect. `returnTo` (the deep-link destination
   * the user was trying to reach) is stashed in sessionStorage so it can
   * survive the external OAuth round-trip and be restored by AuthCallback.
   */
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Sets the authenticated user directly (bypassing the /auth/me bootstrap) —
   * used by AuthCallback (Entra) and the dev sign-in panel, both of which
   * already have a full session-establishment response in hand.
   *
   * `isVacationReviewer` is a SEPARATE optional argument, not a `user` field,
   * because it isn't part of `AuthenticatedUser` (see that flag's own doc
   * comment above). Callers with a fresh sign-in response (AuthCallback,
   * dev-login) MUST pass it — omitting it PRESERVES the current value rather
   * than resetting to `false`, so unrelated `setUser` calls (e.g. PATCH /me
   * profile updates in Settings, whose response never carries this flag) don't
   * regress an already-hydrated reviewer's inbox visibility.
   */
  setUser: (
    user: AuthenticatedUser,
    isVacationReviewer?: boolean,
    canCreateTaskAnywhere?: boolean,
  ) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isVacationReviewer, setIsVacationReviewer] = useState(false);
  // Defaults FALSE, like isVacationReviewer: better to reveal the button a beat late than to
  // show it and snatch it away — and a flash of a control that would 403 is exactly what #883
  // set out to remove.
  const [canCreateTaskAnywhere, setCanCreateTaskAnywhere] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // On mount, check if user is already authenticated
  useEffect(() => {
    let cancelled = false;

    async function checkAuth(): Promise<void> {
      // NP-P65a: ALWAYS probe /auth/me on mount. The session is carried entirely by the HttpOnly
      // `a8planner_access_token` cookie (set by the Entra ID / dev-login flows), which JS cannot read;
      // the api-client sends `credentials: 'include'` on every request, so getMe() succeeds on the
      // cookie alone.
      //
      // `suppressAuthRedirect` prevents api-client's global 401 handler from
      // firing a hard `window.location.href = '/login'` for this bootstrap
      // call — a genuine 401 here just means "not logged in," which we
      // handle locally below, not a redirect-worthy session expiry.
      try {
        const response = await authApi.getMe({ suppressAuthRedirect: true });
        if (!cancelled) {
          setUser(response.data.user);
          setIsVacationReviewer(response.data.isVacationReviewer);
          // `?? true` is deliberate, and it is about VERSION SKEW, not about permissions.
          //
          // The web and the API deploy separately (SWA vs Function App). If the web lands first,
          // this field is simply absent from /auth/me, and a falsy default would hide the create
          // button from EVERY user until the API caught up — a feature blackout produced by a
          // purely cosmetic gate. That exact shape bit us on localhost within a day of shipping
          // #885: an API 36 commits behind served no field, and the owner lost the "+" as a
          // manager.
          //
          // Failing open costs nothing, because this flag has never been the authority:
          // `TaskService.create` re-derives and enforces per list on every write. The worst case
          // is the pre-#885 behaviour — a button that 403s — which the product lived with for
          // years. The worst case of failing closed is nobody can create anything.
          //
          // The initial `useState(false)` above is a DIFFERENT question (don't flash a control
          // and snatch it back while /auth/me is still in flight) and stays false.
          setCanCreateTaskAnywhere(response.data.canCreateTaskAnywhere ?? true);
        }
      } catch {
        // No valid cookie — genuinely logged out.
        if (!cancelled) {
          setUser(null);
          setIsVacationReviewer(false);
          setCanCreateTaskAnywhere(false);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  // Batch B (#47): server-stored preferences are authoritative once we know
  // who the user is — hydrate language (i18next + a8planner_lang mirror) and theme
  // (Zustand store) from the profile. Later changes flow the other way via
  // PATCH /me from the Settings sections, so the two stay converged.
  const setTheme = useUIStore((s) => s.setTheme);
  const setTransparency = useUIStore((s) => s.setTransparency);
  useEffect(() => {
    if (!user) return;
    applyUserLanguage(user.language);
    if (useUIStore.getState().theme !== user.theme) {
      setTheme(user.theme);
    }
    // GHE #1279 — la transparencia viaja igual que el tema, que es lo que hace que la preferencia
    // siga a la persona entre el PC de la oficina y el portátil de casa.
    //
    // ⚠️ `?? 'system'` deliberado aunque el tipo la declare REQUERIDA: si el web va por delante del
    // api en un despliegue, el campo llega `undefined` y TypeScript no lo ve. Degradar a «lo que
    // diga el sistema» es la dirección segura; escribir `undefined` en el store dejaría el `<html>`
    // con las dos clases quitadas y la pantalla de Ajustes sin ninguna tarjeta marcada.
    const deseada = user.transparency ?? 'system';
    if (useUIStore.getState().transparency !== deseada) {
      setTransparency(deseada);
    }
  }, [user, setTheme, setTransparency]);

  const login = useCallback(async (returnTo?: string) => {
    // Fetch the login URL and CSRF state from the backend,
    // store state in sessionStorage, then redirect to Entra ID.
    const response = await authApi.getLoginUrl();
    sessionStorage.setItem('oauth_state', response.data.state);
    // NP-P65a: stash the deep-link destination so AuthCallback can restore
    // it after the external OAuth redirect completes. Cleared (not left
    // stale) when there is nothing to restore.
    if (returnTo) {
      sessionStorage.setItem('oauth_returnTo', returnTo);
    } else {
      sessionStorage.removeItem('oauth_returnTo');
    }
    window.location.href = response.data.url;
  }, []);

  const logout = useCallback(async () => {
    // Server clears the HttpOnly `a8planner_access_token` cookie (the only session state); then clear
    // local React state. Swallow API errors — local state must clear regardless so the user is
    // never stuck signed-in.
    try {
      await authApi.logout();
    } catch {
      // ignore — proceed to local cleanup
    }
    setUser(null);
    setIsVacationReviewer(false);
    window.location.href = '/login';
  }, []);

  const handleSetUser = useCallback(
    (u: AuthenticatedUser, vacationReviewer?: boolean, createAnywhere?: boolean) => {
      setUser(u);
      // Only touch isVacationReviewer when the caller actually supplies it —
      // see the AuthContextValue.setUser doc comment for why omitting it must
      // PRESERVE the current value, not reset to false.
      if (vacationReviewer !== undefined) {
        setIsVacationReviewer(vacationReviewer);
      }
      // Same preserve-on-omit rule as above: a PATCH /me that only changes a theme preference must
      // not silently revoke the create button.
      if (createAnywhere !== undefined) {
        setCanCreateTaskAnywhere(createAnywhere);
      }
      setIsLoading(false);
    },
    [],
  );

  const value: AuthContextValue = {
    user,
    canCreateTaskAnywhere,
    isVacationReviewer,
    isAuthenticated: user !== null,
    isLoading,
    login,
    logout,
    setUser: handleSetUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
